import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Gauge, Loader2 } from "lucide-react";

const fmtMonth = (m) => new Date(`${m}-01T12:00:00Z`).toLocaleDateString(undefined, { month: "short", year: "numeric" });
const usd = (v) => `$${(v || 0).toFixed(2)}`;

export default function GoogleUsageWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/google-usage").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, []);

  if (!data) return <div className="rounded-2xl border border-border bg-card p-5 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  if (data.error) return null;
  const pct = Math.min(100, Math.round((data.requests / data.freeCap) * 100));
  const over = data.requests > data.freeCap;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="google-usage-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center"><Gauge className="w-4 h-4 text-primary" /></div>
        <div>
          <h3 className="font-heading font-bold">Google Places usage · {fmtMonth(data.month)}</h3>
          <p className="text-xs text-muted-foreground">Requests used by prospect syncs this month and the estimated cost.</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div><div className="text-2xl font-bold tabular-nums" data-testid="google-usage-requests">{data.requests.toLocaleString()}</div><div className="text-xs text-muted-foreground">requests</div></div>
        <div><div className="text-2xl font-bold tabular-nums" data-testid="google-usage-free-left">{data.freeLeft.toLocaleString()}</div><div className="text-xs text-muted-foreground">free left</div></div>
        <div><div className={`text-2xl font-bold tabular-nums ${over ? "text-amber-500" : ""}`} data-testid="google-usage-cost">{usd(data.estCost)}</div><div className="text-xs text-muted-foreground">est. cost</div></div>
      </div>
      <Progress value={pct} className="h-2" data-testid="google-usage-bar" />
      <div className="text-[11px] text-muted-foreground mt-1">{pct}% of the {data.freeCap.toLocaleString()} free monthly requests · then ~${data.ratePer1000} per 1,000</div>
      {data.history.length > 1 && (
        <div className="mt-4 border-t border-border pt-3 space-y-1" data-testid="google-usage-history">
          {data.history.slice(1).map((h) => (
            <div key={h.month} className="flex justify-between text-xs"><span className="text-muted-foreground">{fmtMonth(h.month)}</span><span className="tabular-nums">{h.requests.toLocaleString()} req · {usd(h.estCost)}</span></div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-3">Estimate only. Your Google Cloud billing page is the final word, and the free allowance is shared with any other projects on the same billing account.</p>
    </div>
  );
}
